import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subscription, catchError, finalize, first, forkJoin, tap } from 'rxjs';
import { CURRENCY_SELECT_OPTIONS } from 'src/app/core/constants/currency-options.constant';
import { InventoryItemCurrencies } from 'src/app/core/enums/inventory-item-currencies.enum';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemUnitPricing } from 'src/app/core/models/inventory/inventory-item-unit-pricing.interface';
import { InventoryItemUnitPricingUpdate } from 'src/app/core/models/inventory/inventory-item-unit-pricing-update.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';
import { InventoryItemUomAndPricesRowFormGroup } from './inventory-item-uom-and-prices-form.type';
import { InventoryItemUomAndPricesRowView } from './inventory-item-uom-and-prices-row-view.interface';
import { optionalDecimalValidator, requiredDecimalValidator } from './inventory-item-uom-and-prices.validators';

@Component({
  selector: 'app-inventory-item-uom-and-prices-tab',
  templateUrl: './inventory-item-uom-and-prices-tab.component.html',
  styleUrl: './inventory-item-uom-and-prices-tab.component.scss',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  standalone: true,
})
export class InventoryItemUomAndPricesTabComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Input() public unitsOfMeasureOptions: UnitOfMeasureLookup[] = [];
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly availableUnits$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);
  public readonly rowViewModels$ = new BehaviorSubject<InventoryItemUomAndPricesRowView[]>([]);
  public readonly currencyOptions = CURRENCY_SELECT_OPTIONS;
  public readonly form = this.formBuilder.group({
    selectedUnitOfMeasureId: this.formBuilder.control<number | null>(null),
    rows: this.formBuilder.array<InventoryItemUomAndPricesRowFormGroup>([]),
  });
  public isLoading = false;
  public isSaving = false;
  public isEditMode = false;
  public hasPendingChanges = false;
  public canEnterEditMode = false;
  public canCancelEdit = false;
  public canAddUnit = false;
  public canSave = false;

  private readonly subscriptions = new Subscription();
  private currentUnitsOfMeasureOptions: UnitOfMeasureLookup[] = [];

  constructor() {
    this.subscriptions.add(
      this.form.valueChanges
        .pipe(
          tap(() => {
            this.rebuildRowMetadata();
            this.syncActionState();
          }),
        )
        .subscribe(),
    );
  }

  public get rowForms(): FormArray<InventoryItemUomAndPricesRowFormGroup> {
    return this.form.controls.rows;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['companyId'] &&
      !changes['itemId'] &&
      !changes['inventoryItem'] &&
      !changes['unitsOfMeasureOptions']
    ) {
      return;
    }

    this.currentUnitsOfMeasureOptions = this.unitsOfMeasureOptions;
    if (this.inventoryItem) {
      this.syncInventoryItemState(this.inventoryItem, this.currentUnitsOfMeasureOptions);
      return;
    }

    this.loadTabData();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onStartEdit(): void {
    if (!this.canEnterEditMode) {
      return;
    }

    this.isEditMode = true;
    this.form.controls.selectedUnitOfMeasureId.patchValue(null, { emitEvent: false });
    this.syncActionState();
  }

  public onCancelEdit(): void {
    if (!this.canCancelEdit) {
      return;
    }

    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    this.syncInventoryItemState(currentItem, this.currentUnitsOfMeasureOptions);
  }

  public onAddUnit(): void {
    if (!this.isEditMode) {
      return;
    }

    const selectedUnitOfMeasureId = this.form.controls.selectedUnitOfMeasureId.value;
    if (selectedUnitOfMeasureId === null) {
      return;
    }

    const selectedUnit = this.currentUnitsOfMeasureOptions.find((option) => option.id === selectedUnitOfMeasureId);
    if (!selectedUnit || this.rowForms.controls.some((row) => row.controls.unitOfMeasureId.value === selectedUnitOfMeasureId)) {
      return;
    }

    this.rowForms.push(
      this.createRowForm({
        unitOfMeasureId: selectedUnit.id,
        code: selectedUnit.code,
        displayName: selectedUnit.displayName,
        qtyPerUnitOfMeasure: 1,
        height: null,
        width: null,
        length: null,
        cubage: null,
        weight: null,
        unitPrice: null,
        unitCost: null,
        currencyCode: this.resolveCurrencyCode(this.inventoryItem$.getValue()?.currencyCode),
        priceIncludesTax: this.inventoryItem$.getValue()?.priceIncludesTax ?? false,
      }),
    );

    this.form.controls.selectedUnitOfMeasureId.patchValue(null);
    this.rowForms.markAsDirty();
    this.rebuildRowMetadata();
    this.syncActionState();
  }

  public onRemoveUnit(unitOfMeasureId: number): void {
    if (!this.isEditMode) {
      return;
    }

    const rowIndex = this.rowForms.controls.findIndex(
      (row) => row.controls.unitOfMeasureId.value === unitOfMeasureId,
    );
    if (rowIndex < 0) {
      return;
    }

    this.rowForms.removeAt(rowIndex);
    this.rowForms.markAsDirty();
    this.rebuildRowMetadata();
    this.syncActionState();
  }

  public onSave(): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!this.isEditMode || !currentItem || this.isSaving || !this.form.dirty || this.rowForms.length === 0) {
      return;
    }

    this.isSaving = true;
    this.syncActionState();

    this.inventoryItemService
      .updateInventoryItemUnitPricing(this.companyId, this.itemId, this.buildUpdatePayload(currentItem))
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.currentUnitsOfMeasureOptions);
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          if (isConcurrencyConflictError(error)) {
            this.syncInventoryItemState(currentItem, this.currentUnitsOfMeasureOptions);
            this.concurrencyConflict.emit();
          }

          return EMPTY;
        }),
        finalize(() => {
          this.isSaving = false;
          this.syncActionState();
        }),
      )
      .subscribe();
  }

  private loadTabData(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      inventoryItem: this.inventoryItemService.getInventoryItem(this.companyId, this.itemId),
      unitsOfMeasureOptions: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
    })
      .pipe(
        first(),
        tap(({ inventoryItem, unitsOfMeasureOptions }) => {
          this.currentUnitsOfMeasureOptions = unitsOfMeasureOptions;
          this.syncInventoryItemState(inventoryItem, unitsOfMeasureOptions);
        }),
        catchError(() => {
          this.inventoryItem$.next(null);
          this.availableUnits$.next([]);
          this.rowViewModels$.next([]);
          this.rowForms.clear();
          this.isEditMode = false;
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
          this.syncActionState();
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(inventoryItem: InventoryItem, unitsOfMeasureOptions: UnitOfMeasureLookup[]): void {
    this.inventoryItem$.next(inventoryItem);
    this.currentUnitsOfMeasureOptions = unitsOfMeasureOptions;
    this.replaceRows(this.buildUnitPricingRows(inventoryItem, unitsOfMeasureOptions));
    this.form.controls.selectedUnitOfMeasureId.patchValue(null, { emitEvent: false });
    this.form.markAsPristine();
    this.isEditMode = false;
    this.rebuildRowMetadata();
    this.syncActionState();
  }

  private replaceRows(rows: InventoryItemUnitPricing[]): void {
    this.rowForms.clear({ emitEvent: false });
    rows.forEach((row) => this.rowForms.push(this.createRowForm(row), { emitEvent: false }));
  }

  private createRowForm(row: InventoryItemUnitPricing): InventoryItemUomAndPricesRowFormGroup {
    return this.formBuilder.group({
      unitOfMeasureId: this.formBuilder.nonNullable.control(row.unitOfMeasureId),
      qtyPerUnitOfMeasure: this.formBuilder.nonNullable.control(this.toRequiredControlNumber(row.qtyPerUnitOfMeasure), {
        validators: [requiredDecimalValidator(0.0001)],
      }),
      height: this.formBuilder.control(this.toOptionalControlNumber(row.height), {
        validators: [optionalDecimalValidator(0)],
      }),
      width: this.formBuilder.control(this.toOptionalControlNumber(row.width), {
        validators: [optionalDecimalValidator(0)],
      }),
      length: this.formBuilder.control(this.toOptionalControlNumber(row.length), {
        validators: [optionalDecimalValidator(0)],
      }),
      cubage: this.formBuilder.control(this.toOptionalControlNumber(row.cubage), {
        validators: [optionalDecimalValidator(0)],
      }),
      weight: this.formBuilder.control(this.toOptionalControlNumber(row.weight), {
        validators: [optionalDecimalValidator(0)],
      }),
      unitPrice: this.formBuilder.control(this.toOptionalControlNumber(row.unitPrice), {
        validators: [requiredDecimalValidator(0)],
      }),
      unitCost: this.formBuilder.control(this.toOptionalControlNumber(row.unitCost), {
        validators: [requiredDecimalValidator(0)],
      }),
      currencyCode: this.formBuilder.nonNullable.control(this.resolveCurrencyCode(row.currencyCode)),
      priceIncludesTax: this.formBuilder.nonNullable.control(row.priceIncludesTax),
    });
  }

  private buildUnitPricingRows(
    inventoryItem: InventoryItem,
    unitsOfMeasureOptions: UnitOfMeasureLookup[],
  ): InventoryItemUnitPricing[] {
    if (inventoryItem.unitPricing && inventoryItem.unitPricing.length > 0) {
      return this.sortRowsByBaseUnit(inventoryItem.unitPricing, inventoryItem.baseUnitOfMeasureId ?? null);
    }

    const unitLookup = new Map(unitsOfMeasureOptions.map((option) => [option.id, option]));
    const fallbackRows: InventoryItemUnitPricing[] = (inventoryItem.unitsOfMeasure ?? []).map((unit) => ({
      unitOfMeasureId: unit.unitOfMeasureId,
      code: unit.code,
      displayName: unit.displayName,
      qtyPerUnitOfMeasure: unit.qtyPerUnitOfMeasure,
      height: unit.height,
      width: unit.width,
      length: unit.length,
      cubage: unit.cubage,
      weight: unit.weight,
      unitPrice: inventoryItem.unitPrice,
      unitCost: inventoryItem.unitCost,
      currencyCode: this.resolveCurrencyCode(inventoryItem.currencyCode),
      priceIncludesTax: inventoryItem.priceIncludesTax,
    }));

    if (!fallbackRows.length && inventoryItem.baseUnitOfMeasureId) {
      const baseUnit = unitLookup.get(inventoryItem.baseUnitOfMeasureId);
      fallbackRows.push({
        unitOfMeasureId: inventoryItem.baseUnitOfMeasureId,
        code: baseUnit?.code ?? '',
        displayName: baseUnit?.displayName ?? inventoryItem.unit ?? '',
        qtyPerUnitOfMeasure: 1,
        height: inventoryItem.height ?? null,
        width: inventoryItem.width ?? null,
        length: inventoryItem.length ?? null,
        cubage: inventoryItem.cubage ?? null,
        weight: inventoryItem.weight ?? null,
        unitPrice: null,
        unitCost: null,
        currencyCode: this.resolveCurrencyCode(inventoryItem.currencyCode),
        priceIncludesTax: inventoryItem.priceIncludesTax,
      });
    }

    return this.sortRowsByBaseUnit(fallbackRows, inventoryItem.baseUnitOfMeasureId ?? null);
  }

  private sortRowsByBaseUnit(rows: InventoryItemUnitPricing[], baseUnitOfMeasureId: number | null): InventoryItemUnitPricing[] {
    return [...rows].sort((left, right) => {
      if (left.unitOfMeasureId === baseUnitOfMeasureId) {
        return -1;
      }

      if (right.unitOfMeasureId === baseUnitOfMeasureId) {
        return 1;
      }

      return left.displayName.localeCompare(right.displayName);
    });
  }

  private rebuildRowMetadata(): void {
    const currentItem = this.inventoryItem$.getValue();
    const baseUnitOfMeasureId = currentItem?.baseUnitOfMeasureId ?? null;
    const selectedUnitIds = new Set(this.rowForms.controls.map((row) => row.controls.unitOfMeasureId.value));
    const unitLookup = new Map(this.currentUnitsOfMeasureOptions.map((option) => [option.id, option]));

    this.rowViewModels$.next(
      this.rowForms.controls.map((row) => {
        const unitOfMeasureId = row.controls.unitOfMeasureId.value;
        const unitOption = unitLookup.get(unitOfMeasureId);
        const isBase = unitOfMeasureId === baseUnitOfMeasureId;

        return {
          unitOfMeasureId,
          displayName: unitOption?.displayName ?? '',
          code: unitOption?.code ?? '',
          isBase,
          canRemove: !isBase,
          qtyPerUnitOfMeasure: row.controls.qtyPerUnitOfMeasure.value,
          height: row.controls.height.value,
          width: row.controls.width.value,
          length: row.controls.length.value,
          cubage: row.controls.cubage.value,
          weight: row.controls.weight.value,
          unitPrice: row.controls.unitPrice.value,
          unitCost: row.controls.unitCost.value,
          currencyCode: row.controls.currencyCode.value,
          currencyLabelKey: row.controls.currencyCode.value
            ? `INVENTORY_ITEM_CURRENCIES.${this.resolveCurrencyCode(row.controls.currencyCode.value)}`
            : null,
          priceIncludesTaxLabelKey: row.controls.priceIncludesTax.value ? 'BUTTONS.YES' : 'BUTTONS.NO',
        };
      }),
    );

    this.availableUnits$.next(this.currentUnitsOfMeasureOptions.filter((option) => !selectedUnitIds.has(option.id)));
  }

  private buildUpdatePayload(inventoryItem: InventoryItem): InventoryItemUnitPricingUpdate {
    const baseUnitOfMeasureId = inventoryItem.baseUnitOfMeasureId ?? null;

    return {
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
      rows: this.rowForms.controls.map((row) => this.buildUpdateRow(row, baseUnitOfMeasureId)),
    };
  }

  private buildUpdateRow(row: InventoryItemUomAndPricesRowFormGroup, baseUnitOfMeasureId: number | null) {
    const unitOfMeasureId = row.controls.unitOfMeasureId.value;

    return {
      unitOfMeasureId,
      qtyPerUnitOfMeasure: unitOfMeasureId === baseUnitOfMeasureId ? 1 : this.toNumber(row.controls.qtyPerUnitOfMeasure.value, 1),
      height: this.toOptionalNumber(row.controls.height.value),
      width: this.toOptionalNumber(row.controls.width.value),
      length: this.toOptionalNumber(row.controls.length.value),
      cubage: this.toOptionalNumber(row.controls.cubage.value),
      weight: this.toOptionalNumber(row.controls.weight.value),
      unitPrice: this.toNumber(row.controls.unitPrice.value, 0),
      unitCost: this.toNumber(row.controls.unitCost.value, 0),
      currencyCode: this.toOptionalString(row.controls.currencyCode.value),
      priceIncludesTax: row.controls.priceIncludesTax.value,
    };
  }

  private syncActionState(): void {
    this.hasPendingChanges = this.form.dirty;
    this.canEnterEditMode =
      !!this.inventoryItem$.getValue() &&
      this.inventoryItem$.getValue()?.isArchived !== true &&
      !this.isLoading &&
      !this.isSaving &&
      !this.isEditMode;
    this.canCancelEdit = this.isEditMode && !this.isSaving;
    this.canAddUnit =
      this.isEditMode &&
      !this.isSaving &&
      this.availableUnits$.getValue().length > 0 &&
      this.form.controls.selectedUnitOfMeasureId.value !== null;
    this.canSave = this.isEditMode && this.hasPendingChanges && this.form.valid && !this.isSaving && this.rowForms.length > 0;
  }

  private toRequiredControlNumber(value?: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    return `${value}`;
  }

  private toOptionalControlNumber(value?: number | null): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return `${value}`;
  }

  private toNumber(value: string | number | null, fallbackValue = 0): number {
    const normalizedValue = this.normalizeNumericControlValue(value);
    if (normalizedValue === null) {
      return fallbackValue;
    }

    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
  }

  private toOptionalNumber(value: string | number | null): number | null {
    const normalizedValue = this.normalizeNumericControlValue(value);
    if (normalizedValue === null) {
      return null;
    }

    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private normalizeNumericControlValue(
    value: string | number | null,
  ): string | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? `${value}` : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private toOptionalString(value: string | null): string | null {
    if (value === null) {
      return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private resolveCurrencyCode(value?: string | null): InventoryItemCurrencies {
    if (value && Object.values(InventoryItemCurrencies).includes(value as InventoryItemCurrencies)) {
      return value as InventoryItemCurrencies;
    }

    return InventoryItemCurrencies.EUR;
  }
}
